import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { fireTradeToast, fireTradeLoading } from "../../lib/tradeToast";
import { motion, AnimatePresence } from "framer-motion";
import SavvyInsight from "../SavvyInsight/SavvyInsight";
import { buildApiUrl } from "../../lib/api";
import {
    fetchEarningsOpportunity,
    getEarningsOpportunityNotice,
} from "../../lib/earningsOpportunity";
import { buildSimulationLaunchRequest, formatStrategyLabel } from "../../lib/strategySimulation";
import { formatExecutionError } from "../../lib/executionError";
import {
    isSmallAccountMode,
    isStrategyAllowedForBuyingPower,
    getStrategyBlockMessage,
    getWheelCapitalWarning,
    EARNINGS_PREVIEW_ONLY,
    SMALL_ACCOUNT_REJECTION_MESSAGE,
    SMALL_ACCOUNT_TOOLTIP,
} from "../../lib/smallAccountMode";
import "./SuggestedTrade.css";

// ------------------------------------------------------------------ //
// Animation variants                                                   //
// ------------------------------------------------------------------ //

const cardVariants = {
    hidden:  { opacity: 0, y: 12 },
    visible: { opacity: 1, y: 0,  transition: { duration: 0.28, ease: "easeOut" } },
    hover:   { scale: 1.012,      transition: { duration: 0.2,  ease: "easeOut" } },
    tap:     { scale: 0.988,      transition: { duration: 0.1,  ease: "easeOut" } },
};

const contentVariants = {
    hidden:  { opacity: 0, y: 8  },
    visible: { opacity: 1, y: 0, transition: { duration: 0.22, ease: "easeOut" } },
    exit:    { opacity: 0, y: -6, transition: { duration: 0.15, ease: "easeIn"  } },
};

// ------------------------------------------------------------------ //
// Helpers                                                              //
// ------------------------------------------------------------------ //

function parseApiError(raw) {
    if (!raw) return "Unable to load suggestion.";
    if (typeof raw === "object") {
        raw = raw.message || raw.error || raw.tooltip || JSON.stringify(raw);
    }
    if (typeof raw === "string" && raw.trim().startsWith("{")) {
        try {
            const parsed = JSON.parse(raw);
            raw = parsed.message || parsed.error || parsed.details || raw;
        } catch (_) {}
    }
    const lower = String(raw).toLowerCase();
    if (lower.includes("too many requests") || lower.includes("rate limit") || lower.includes("rate_limited") || /^\d+ per \d+ (minute|second|hour)/.test(lower))
        return "Market data rate limit reached. Wait a moment and try again.";
    if (lower.includes("invalid") && lower.includes("symbol"))
        return "Invalid symbol. Please check the ticker and try again.";
    if (lower.includes("market data unavailable") || lower.includes("no options contracts"))
        return "No options data available for this symbol.";
    return String(raw);
}

function fmtCurrency(n) {
    return Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtCompactCurrency(n) {
    return Number(n).toLocaleString("en-US", { maximumFractionDigits: 0 });
}

function fmtDate(dateStr) {
    if (!dateStr) return "—";
    const date = new Date(`${dateStr.slice(0, 10)}T00:00:00`);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function fmtTime(date) {
    if (!date) return "";
    return date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

const DRIFT_THRESHOLD = 0.10;
const EXECUTION_TIMEOUT_MS = 30000;

function formatDaysAway(days) {
    if (days == null) return null;
    return `${days}d away`;
}

function formatOpportunityStrategyLabel(strategy) {
    if (strategy === "call_debit_spread") return "Call debit spread";
    if (strategy === "put_debit_spread") return "Put debit spread";
    return "Debit spread";
}

function buildExecutionToastMessage(execution, payload = {}) {
    const execStatus = String(execution?.status || "").toLowerCase();
    if (execStatus === "submitted") return { message: "Trade executed", type: "success" };
    if (execStatus === "queued") return { message: "Trade queued · Executes at market open", type: "info" };
    if (execStatus === "error") {
        return { message: execution?.error || payload?.error || "Execution failed", type: "error" };
    }
    if (execStatus === "dry_run") {
        if (payload?.lifecycle_preview_only) {
            return {
                message: payload?.lifecycle_preview_reason || "This strategy is currently preview-only, so no live order was placed.",
                type: "info",
            };
        }
        if (payload?.earnings_preview_only) {
            return {
                message: payload?.earnings_preview_reason || "Earnings strategy is currently preview-only, so no live order was placed.",
                type: "info",
            };
        }
        return { message: execution?.message || "Dry run complete — no order placed", type: "info" };
    }
    if (execStatus === "skipped") {
        return { message: execution?.message || "No order was placed.", type: "info" };
    }
    return {
        message: execution?.message || payload?.message || "Trade request completed.",
        type: "info",
    };
}

function formatRejectionReason(reason) {
    const labels = {
        low_open_interest: "Low open interest",
        low_volume: "Low volume",
        iv_out_of_range: "IV outside range",
        bid_ask_too_wide: "Bid/ask too wide",
        missing_bid_ask: "Missing bid/ask quotes",
        invalid_spread_pricing: "Spread pricing did not clear",
        invalid_contract: "Invalid contract",
        invalid_strike_order: "Strike pairing failed",
        non_positive_premium: "Premium was not positive",
        dte_out_of_range: "DTE outside range",
    };
    return labels[reason] || reason.replaceAll("_", " ");
}

const ACTION_LABELS = {
    SELL_PUT:    "Sell Cash-Secured Put",
    SELL_CSP:    "Sell Cash-Secured Put",
    SELL_CALL:   "Sell Covered Call",
    SELL_CC:     "Sell Covered Call",
    TAKE_PROFIT: "Take Profit",
    ROLL:        "Roll Position",
};

const STRATEGY_OPTIONS = [
    { id: "wheel", label: "Wheel Strategy" },
    { id: "tactical_spread", label: "Tactical Spread" },
    { id: "regime_spread", label: "Regime Spread" },
    { id: "earnings", label: "Earnings Strategy" },
];

const ALTERNATE_SYMBOL_CANDIDATES = {
    tactical_spread: ["SPY", "QQQ", "IWM", "AAPL", "MSFT", "NVDA"],
    regime_spread: ["SPY", "QQQ", "IWM", "AAPL", "MSFT", "NVDA"],
    earnings: ["AAPL", "MSFT", "NVDA", "AMZN", "META", "TSLA"],
};

function getTradeQuality(decision) {
    if (!decision?.should_trade || !decision?.meta) {
        return { score: 34, label: "Wait", tone: "cautious" };
    }

    if (decision.meta?.strategy === "earnings" && decision.meta?.score != null) {
        const score = Math.max(0, Math.min(100, Math.round(Number(decision.meta.score))));
        const confidence = String(decision.meta?.confidence || "").toLowerCase();
        if (confidence === "high" || score >= 75) return { score, label: "Strong setup", tone: "strong" };
        if (confidence === "medium" || score >= 60) return { score, label: "Good setup", tone: "good" };
        if (score >= 45) return { score, label: "Mixed setup", tone: "mixed" };
        return { score, label: "Low conviction", tone: "cautious" };
    }

    const delta = Math.abs(Number(decision.meta?.delta ?? 0));
    const dte = Number(decision.meta?.dte ?? 0);
    const premium = Number(decision.price ?? 0);
    const strike = Number(decision.meta?.strike_price ?? 0);
    const premiumYield = strike > 0 ? premium / strike : 0;

    let score = 52;
    if (delta >= 0.18 && delta <= 0.32) score += 12;
    else if (delta > 0.4) score -= 8;

    if (dte >= 7 && dte <= 45) score += 10;
    else if (dte > 60 || dte < 3) score -= 6;

    if (premiumYield >= 0.01 && premiumYield <= 0.03) score += 10;
    else if (premiumYield > 0.04) score -= 6;

    if (decision.meta?.small_account_mode) score += 6;
    if (decision.action === "SELL_CALL" || decision.action === "SELL_CC") score += 4;

    score = Math.max(0, Math.min(100, Math.round(score)));

    if (score >= 78) return { score, label: "Strong setup", tone: "strong" };
    if (score >= 60) return { score, label: "Good setup", tone: "good" };
    if (score >= 45) return { score, label: "Mixed setup", tone: "mixed" };
    return { score, label: "Low conviction", tone: "cautious" };
}

function getEarningsConfidence(score, confidence) {
    const numericScore = Number(score);
    if (Number.isFinite(numericScore)) {
        if (numericScore >= 75) return "HIGH";
        if (numericScore >= 50) return "MEDIUM";
        return "LOW";
    }

    const normalized = String(confidence || "").trim().toLowerCase();
    if (normalized === "high" || normalized === "medium" || normalized === "low") {
        return normalized.toUpperCase();
    }

    return "";
}

function buildDisplayDecision(decision, earningsData) {
    if (!decision?.meta?.strategy || decision.meta.strategy !== "earnings" || !earningsData) {
        return decision;
    }

    const selectedOpportunity = earningsData?.opportunities?.find(
        (opportunity) => opportunity.strategy === decision.meta?.opportunity_strategy
    ) ?? earningsData?.best_opportunity;

    if (!selectedOpportunity) return decision;

    return {
        ...decision,
        price: Number(selectedOpportunity.debit ?? decision.price ?? 0),
        meta: {
            ...decision.meta,
            opportunity_strategy: selectedOpportunity.strategy || decision.meta.opportunity_strategy,
            earnings_date: selectedOpportunity.earnings_date || earningsData?.earnings?.earnings_date || decision.meta.earnings_date,
            days_to_earnings: selectedOpportunity.days_to_earnings ?? earningsData?.earnings?.days_to_earnings ?? decision.meta.days_to_earnings,
            debit: selectedOpportunity.debit ?? decision.meta.debit,
            width: selectedOpportunity.width ?? decision.meta.width,
            long_strike: selectedOpportunity.long_strike ?? decision.meta.long_strike,
            short_strike: selectedOpportunity.short_strike ?? decision.meta.short_strike,
            dte: selectedOpportunity.spread_dte ?? selectedOpportunity.days_to_earnings ?? decision.meta.dte,
            score: selectedOpportunity.score ?? decision.meta.score,
            confidence: selectedOpportunity.confidence ?? decision.meta.confidence,
            iv_rank: earningsData?.earnings?.iv_rank ?? decision.meta.iv_rank,
            underlying_price: earningsData?.earnings?.price ?? decision.meta.underlying_price,
        },
    };
}

function buildEarningsDisplayDecision(symbol, earningsData, existingDecision) {
    if (!earningsData) return existingDecision;

    const selectedOpportunity = earningsData?.best_opportunity
        ?? earningsData?.opportunities?.[0]
        ?? null;

    if (!selectedOpportunity) {
        return {
            action: "NO_ACTION",
            should_trade: false,
            reason: earningsData?.synthetic
                ? "The earnings date is saved, but there is no live spread setup yet."
                : `No earnings spread setup is available for ${symbol} right now.`,
            meta: {
                strategy: "earnings",
                earnings_date: earningsData?.earnings?.earnings_date,
                days_to_earnings: earningsData?.earnings?.days_to_earnings,
                iv_rank: earningsData?.earnings?.iv_rank ?? null,
                underlying_price: earningsData?.earnings?.price ?? null,
            },
        };
    }

    return {
        ...(existingDecision || {}),
        action: "OPEN_DEBIT_SPREAD",
        should_trade: true,
        reason: `Earnings setup found for ${symbol}.`,
        price: Number(selectedOpportunity.debit ?? existingDecision?.price ?? 0),
        meta: {
            ...(existingDecision?.meta || {}),
            strategy: "earnings",
            opportunity_strategy: selectedOpportunity.strategy,
            long_strike: selectedOpportunity.long_strike,
            short_strike: selectedOpportunity.short_strike,
            width: selectedOpportunity.width,
            debit: selectedOpportunity.debit,
            dte: selectedOpportunity.spread_dte ?? selectedOpportunity.days_to_earnings,
            score: selectedOpportunity.score,
            confidence: selectedOpportunity.confidence,
            earnings_date: selectedOpportunity.earnings_date || earningsData?.earnings?.earnings_date,
            days_to_earnings: selectedOpportunity.days_to_earnings ?? earningsData?.earnings?.days_to_earnings,
            iv_rank: earningsData?.earnings?.iv_rank ?? existingDecision?.meta?.iv_rank,
            underlying_price: earningsData?.earnings?.price ?? existingDecision?.meta?.underlying_price,
        },
    };
}

function buildWhyTrade(decision, symbol) {
    if (!decision?.meta) return [];
    if (decision.meta?.opportunity_strategy) {
        const direction = decision.meta.opportunity_strategy === "call_debit_spread" ? "bullish" : "bearish";
        const debit = Number(decision.meta?.debit ?? decision.price ?? 0);
        const width = Number(decision.meta?.width ?? 0);
        const bullets = [
            `This ${direction} setup uses a ${formatOpportunityStrategyLabel(decision.meta.opportunity_strategy).toLowerCase()} into earnings.`,
        ];
        if (decision.meta?.earnings_date) {
            bullets.push(
                `The next event is expected ${fmtDate(decision.meta.earnings_date)}${decision.meta?.days_to_earnings != null ? ` · ${formatDaysAway(decision.meta.days_to_earnings)}` : ""}.`
            );
        }
        if (debit > 0 && width > 0) {
            bullets.push(`You pay about $${fmtCurrency(debit * 100)} to control a ${width.toFixed(0)}-point spread.`);
        }
        return bullets.slice(0, 3);
    }
    const delta = decision.meta?.delta;
    const dte = decision.meta?.dte;
    const strike = decision.meta?.strike_price;
    const premium = decision.price;
    const bullets = [];

    if (premium != null) {
        bullets.push(`Collect about $${fmtCurrency(Number(premium) * 100)} in premium upfront.`);
    }
    if (delta != null) {
        bullets.push(`Delta near ${Number(delta).toFixed(2)} keeps the setup balanced between yield and assignment odds.`);
    }
    if (dte != null) {
        bullets.push(`${dte} days to expiration keeps the trade short enough to manage actively.`);
    }
    if (strike != null) {
        bullets.push(`The strike centers your decision around owning or selling ${symbol} near $${fmtCurrency(strike)}.`);
    }
    return bullets.slice(0, 3);
}

function buildRisks(decision, symbol, hasQueuedOrders) {
    if (!decision?.meta) return [];
    if (decision.meta?.opportunity_strategy) {
        const debit = Number(decision.meta?.debit ?? decision.price ?? 0);
        const width = Number(decision.meta?.width ?? 0);
        const longStrike = Number(decision.meta?.long_strike ?? 0);
        const risks = [];

        if (debit > 0) {
            risks.push(`Your max loss is the debit paid, or about $${fmtCurrency(debit * 100)}.`);
        }
        if (width > 0 && debit > 0) {
            risks.push(`The spread needs a strong post-earnings move because max profit is capped near $${fmtCurrency((width - debit) * 100)}.`);
        }
        if (longStrike > 0) {
            const breakEven = decision.meta.opportunity_strategy === "call_debit_spread"
                ? longStrike + debit
                : longStrike - debit;
            risks.push(`Break-even sits near $${fmtCurrency(breakEven)}, so muted price action can leave the spread underwater.`);
        }
        if (hasQueuedOrders) {
            risks.push("You already have queued trades, so this setup may compete with other ideas.");
        }
        return risks.slice(0, 3);
    }
    const strike = Number(decision.meta?.strike_price ?? 0);
    const premium = Number(decision.price ?? 0);
    const risks = [];

    if (decision.meta?.option_type === "put" && strike > 0) {
        risks.push(`You could be assigned 100 shares of ${symbol} and need about $${fmtCompactCurrency(strike * 100)} in buying power.`);
    }
    if (decision.meta?.option_type === "call" && strike > 0) {
        risks.push(`If ${symbol} rallies, your shares may be called away at $${fmtCurrency(strike)}.`);
    }
    if (strike > 0 && premium > 0) {
        risks.push(`Break-even sits near $${fmtCurrency(strike - premium)}, so downside below that level hurts the trade.`);
    }
    if (hasQueuedOrders) {
        risks.push("You already have queued trades, so this idea may compete for capital.");
    }
    return risks.slice(0, 3);
}

// ------------------------------------------------------------------ //
// Sub-components                                                       //
// ------------------------------------------------------------------ //

// ------------------------------------------------------------------ //
// TradeSimPreview — simulation impact for a specific trade             //
// ------------------------------------------------------------------ //

function TradeSimPreview({ decision, portfolioValue }) {
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);

    const fetch = useCallback(async () => {
        const { strike_price, delta, dte } = decision?.meta ?? {};
        const premium = decision?.price;
        if (!strike_price || !premium || !dte) return;

        setLoading(true);
        try {
            const { data } = await axios.post(
                buildApiUrl("/simulation/trade-preview"),
                {
                    strike:          Number(strike_price),
                    delta:           Number(delta ?? 0),
                    premium:         Number(premium),
                    dte:             Number(dte),
                    initial_capital: portfolioValue ?? 1000,
                },
                { withCredentials: true }
            );
            setPreview(data);
        } catch {
            // non-fatal — widget simply doesn't render
        } finally {
            setLoading(false);
        }
    }, [decision, portfolioValue]);

    useEffect(() => { fetch(); }, [fetch]);

    if (loading) {
        return (
            <div className="st-sim-preview st-sim-preview--loading">
                <div className="st-sim-skel" style={{ width: 80,  height: 9,  borderRadius: 4 }} />
                <div className="st-sim-skel" style={{ width: 120, height: 13, borderRadius: 5 }} />
            </div>
        );
    }

    if (!preview) return null;

    const { prob_of_profit, four_cycle_projection: proj, premium_pct } = preview;
    const p50 = proj?.p50_capital;
    const p10 = proj?.p10_capital;
    const p90 = proj?.p90_capital;
    const cap = portfolioValue ?? 1000;

    return (
        <div className="st-sim-preview">
            <div className="st-section-head">
                <div className="st-section-title">Simulation outlook</div>
                <div className="st-section-subtitle">Modeled over four trade cycles</div>
            </div>
            <div className="st-sim-row">
                <div className="st-sim-stat">
                    <span className="st-sim-stat-label">Win prob</span>
                    <span
                        className="st-sim-stat-value"
                        style={{ color: prob_of_profit >= 0.65 ? "#4ade80" : prob_of_profit >= 0.50 ? "#fbbf24" : "#f87171" }}
                    >
                        {(prob_of_profit * 100).toFixed(0)}%
                    </span>
                </div>
                <div className="st-sim-divider" />
                <div className="st-sim-stat">
                    <span className="st-sim-stat-label">Yield / trade</span>
                    <span className="st-sim-stat-value st-sim-green">
                        {(premium_pct * 100).toFixed(2)}%
                    </span>
                </div>
                <div className="st-sim-divider" />
                {p50 != null && (
                    <div className="st-sim-stat">
                        <span className="st-sim-stat-label">Median capital</span>
                        <span
                            className="st-sim-stat-value"
                            style={{ color: p50 >= cap ? "#4ade80" : "#f87171" }}
                        >
                            ${Number(p50).toLocaleString("en-US", { maximumFractionDigits: 0 })}
                        </span>
                    </div>
                )}
            </div>
            {p10 != null && p90 != null && (
                <div className="st-sim-range">
                    <span style={{ color: "#f87171" }}>
                        ${Number(p10).toLocaleString("en-US", { maximumFractionDigits: 0 })}
                    </span>
                    <span className="st-sim-range-bar">
                        <span
                            className="st-sim-range-fill"
                            style={{ width: `${Math.min(100, ((p50 - p10) / (p90 - p10)) * 100)}%` }}
                        />
                    </span>
                    <span style={{ color: "#4ade80" }}>
                        ${Number(p90).toLocaleString("en-US", { maximumFractionDigits: 0 })}
                    </span>
                </div>
            )}
        </div>
    );
}

function DecisionScore({ decision }) {
    const quality = getTradeQuality(decision);
    return (
        <div className={`st-score-card st-score-card--${quality.tone}`}>
            <div>
                <div className="st-section-title">Trade quality</div>
                <div className="st-section-subtitle">{quality.label}</div>
            </div>
            <div className="st-score-meter">
                <div className="st-score-value">{quality.score}</div>
                <div className="st-score-max">/100</div>
            </div>
        </div>
    );
}

function PortfolioStatusCard({ buyingPower, dailyPnL, hasQueuedOrders, strategyStatus, accountSource, onEditStrategy }) {
    const pnlPositive = (dailyPnL ?? 0) >= 0;
    const strategyLabel = strategyStatus || "Strategy active";
    return (
        <div className="st-portfolio-status">
            <div className="st-section-head">
                <div className="st-section-title">Account status</div>
                <div className="st-section-subtitle">Buying power and trade context</div>
            </div>
            <div className="st-portfolio-grid">
                <div className="st-portfolio-item">
                    <span className="st-portfolio-label">Buying power</span>
                    <strong>{buyingPower != null ? `$${fmtCurrency(buyingPower)}` : "—"}</strong>
                </div>
                <div className="st-portfolio-item">
                    <span className="st-portfolio-label">Today</span>
                    <strong className={pnlPositive ? "st-account-pnl positive" : "st-account-pnl negative"}>
                        {dailyPnL != null ? `${pnlPositive ? "+" : ""}$${fmtCurrency(dailyPnL)}` : "—"}
                    </strong>
                </div>
                <div className="st-portfolio-item">
                    <span className="st-portfolio-label">Queue</span>
                    <strong>{hasQueuedOrders ? "Orders waiting" : "Clear"}</strong>
                </div>
                <div className="st-portfolio-item">
                    <span className="st-portfolio-label">Strategy</span>
                    <div className="st-strategy-inline">
                        <strong>{strategyLabel}</strong>
                        <button
                            type="button"
                            className="st-strategy-edit"
                            onClick={onEditStrategy}
                            title="Edit strategy"
                            aria-label="Edit strategy"
                        >
                            ✎
                        </button>
                    </div>
                </div>
            </div>
            <div className="st-account-mode-note">
                {accountSource === "broker"
                    ? "Broker mode uses the customer account when broker authentication is available."
                    : "Trader account uses the Alpaca trading account so paper/live strategy work can continue."}
            </div>
        </div>
    );
}

function DecisionBullets({ title, subtitle, items, tone = "neutral" }) {
    if (!items?.length) return null;
    return (
        <div className={`st-bullet-card st-bullet-card--${tone}`}>
            <div className="st-section-head">
                <div className="st-section-title">{title}</div>
                {subtitle ? <div className="st-section-subtitle">{subtitle}</div> : null}
            </div>
            <div className="st-bullet-list">
                {items.map((item) => (
                    <div key={item} className="st-bullet-item">
                        <span className="st-bullet-dot" />
                        <span>{item}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

function EarningsSummary({ decision, earningsData }) {
    const meta = decision?.meta;
    if (!meta?.opportunity_strategy && !meta?.earnings_date) return null;

    const event = earningsData?.earnings || {
        earnings_date: meta?.earnings_date,
        days_to_earnings: meta?.days_to_earnings,
        iv_rank: meta?.iv_rank,
        price: earningsData?.price || meta?.underlying_price,
    };

    const directionLabel = meta?.opportunity_strategy === "put_debit_spread" ? "↓ Bearish" : "↑ Bullish";
    const strategyLabel = formatOpportunityStrategyLabel(meta?.opportunity_strategy);
    const score = meta?.score != null ? Math.round(Number(meta.score)) : null;
    const confidence = getEarningsConfidence(score, meta?.confidence);
    const confidenceClass = confidence.toLowerCase();

    return (
        <div className="st-earnings-panel">
            {event?.earnings_date ? (
                <div className="st-earnings-window">
                    <div className="st-earnings-window-left">
                        <span className="st-earnings-kicker">Next earnings</span>
                        <span className="st-earnings-date">{fmtDate(event.earnings_date)}</span>
                        {event?.days_to_earnings != null ? (
                            <span className="st-earnings-tag">{formatDaysAway(event.days_to_earnings)}</span>
                        ) : null}
                    </div>
                    <div className="st-earnings-meta">
                        {event?.iv_rank != null ? <span>IV Rank <strong>{Number(event.iv_rank).toFixed(0)}</strong></span> : null}
                        {event?.price > 0 ? <span>Price <strong>${Number(event.price).toFixed(2)}</strong></span> : null}
                    </div>
                </div>
            ) : null}

            {meta?.opportunity_strategy ? (
                <div className="st-earnings-opportunity">
                    <div className="st-earnings-opportunity-left">
                        <span className="st-earnings-direction">{directionLabel}</span>
                        <span className="st-earnings-spread">{strategyLabel}</span>
                        {confidence ? (
                            <span className={`st-earnings-confidence st-earnings-confidence--${confidenceClass}`}>
                                {confidence}
                            </span>
                        ) : null}
                        {score != null ? <span className="st-earnings-score">{score}</span> : null}
                    </div>
                </div>
            ) : null}
        </div>
    );
}

function EarningsStatusNotice({ symbol, earningsData }) {
    const message = getEarningsOpportunityNotice(earningsData, symbol);
    if (!message) return null;
    return <div className="st-wheel-warning">{message}</div>;
}

function NoTradeInsightCard({ decision, onConfigure, onOpenSimulation, simulationLaunch, alternateOpportunity, onSelectSymbol }) {
    const reason = decision?.reason || decision?.meta?.reason || "The current setup does not clear the trade bar.";
    const scanDebug = decision?.scan_debug;
    const topCandidates = (scanDebug?.top_candidates || []).slice(0, 5);
    const rejectedTrades = (scanDebug?.rejected_trades || []).slice(0, 5);
    const alternativesConsidered = Number(scanDebug?.alternatives_considered || 0);
    const topRejections = Object.entries(scanDebug?.top_rejection_reasons || {})
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3);
    const hasScannerAudit = Boolean(scanDebug && scanDebug.contracts_scanned > 0);
    const dteRange = scanDebug?.dte_range_used
        ? `${scanDebug.dte_range_used.min}-${scanDebug.dte_range_used.max}d`
        : null;
    const ivRange = scanDebug?.iv_filters
        ? `${scanDebug.iv_filters.min}-${scanDebug.iv_filters.max}`
        : null;
    const liquidityLabel = scanDebug?.liquidity_filters
        ? `OI ${scanDebug.liquidity_filters.min_open_interest}+ · Vol ${scanDebug.liquidity_filters.min_volume}+`
        : null;

    const nextSteps = hasScannerAudit && scanDebug?.candidates_after_liquidity === 0
        ? [
            "Switch to a more liquid symbol or loosen the liquidity gate.",
            "Review IV and DTE filters before forcing a spread.",
            "Run simulation to compare this setup against a friendlier market path.",
        ]
        : [
            "Review trade settings and capital limits.",
            "Try a different symbol with stronger option flow.",
            "Run a simulation before forcing a trade.",
        ];

    return (
        <div className="st-no-trade-card">
            <div className="st-no-trade">
                <div className="st-no-trade-title">No trade right now</div>
                <div className="st-no-trade-reason">{reason}</div>
            </div>

            {hasScannerAudit ? (
                <div className="st-scan-audit">
                    <div className="st-scan-audit-header">
                        <div className="st-scan-audit-title">Why nothing cleared</div>
                        <div className="st-scan-audit-meta">
                            {formatStrategyLabel(scanDebug?.strategy || decision?.meta?.strategy)} scan
                            {alternativesConsidered > 0 ? ` · ${alternativesConsidered} alternatives considered` : ""}
                        </div>
                    </div>

                    <div className="st-scan-grid">
                        <div className="st-scan-stat">
                            <span className="st-scan-label">Scanned</span>
                            <span className="st-scan-value">{scanDebug.contracts_scanned}</span>
                        </div>
                        <div className="st-scan-stat">
                            <span className="st-scan-label">After IV</span>
                            <span className="st-scan-value">{scanDebug.candidates_after_iv ?? 0}</span>
                        </div>
                        <div className="st-scan-stat">
                            <span className="st-scan-label">After liquidity</span>
                            <span className="st-scan-value">{scanDebug.candidates_after_liquidity ?? 0}</span>
                        </div>
                        <div className="st-scan-stat">
                            <span className="st-scan-label">Final setups</span>
                            <span className="st-scan-value">{scanDebug.final_candidates ?? 0}</span>
                        </div>
                    </div>

                    {(dteRange || ivRange || liquidityLabel) ? (
                        <div className="st-scan-filters">
                            {dteRange ? <span className="st-scan-filter">DTE {dteRange}</span> : null}
                            {ivRange ? <span className="st-scan-filter">IV {ivRange}</span> : null}
                            {liquidityLabel ? <span className="st-scan-filter">{liquidityLabel}</span> : null}
                        </div>
                    ) : null}

                    {topRejections.length > 0 ? (
                        <div className="st-scan-rejections">
                            {topRejections.map(([reasonKey, count]) => (
                                <div className="st-scan-rejection" key={reasonKey}>
                                    <span className="st-scan-rejection-name">{formatRejectionReason(reasonKey)}</span>
                                    <span className="st-scan-rejection-count">{count}</span>
                                </div>
                            ))}
                        </div>
                    ) : null}

                    {topCandidates.length > 0 ? (
                        <div className="st-scan-candidate-list">
                            <div className="st-scan-list-title">Top ranked setups</div>
                            {topCandidates.map((candidate) => (
                                <div className="st-scan-candidate" key={`${candidate.long_leg}-${candidate.short_leg}`}>
                                    <div className="st-scan-candidate-main">
                                        <span className="st-scan-candidate-score">{Number(candidate.trade_score || 0).toFixed(1)}</span>
                                        <span className="st-scan-candidate-symbol">
                                            {candidate.symbol} {candidate.dte}d · ${Number(candidate.debit || 0).toFixed(2)} debit
                                        </span>
                                    </div>
                                    <span className="st-scan-candidate-meta">
                                        Trend {Number(candidate.trend_strength || 0).toFixed(2)} · IV {Math.round(Number(candidate.iv_percentile || 0) * 100)}%
                                    </span>
                                </div>
                            ))}
                        </div>
                    ) : null}

                    {rejectedTrades.length > 0 ? (
                        <div className="st-scan-candidate-list">
                            <div className="st-scan-list-title">Rejected after ranking</div>
                            {rejectedTrades.map((candidate) => (
                                <div className="st-scan-candidate st-scan-candidate--rejected" key={`${candidate.long_leg}-${candidate.short_leg}-rejected`}>
                                    <div className="st-scan-candidate-main">
                                        <span className="st-scan-candidate-score">{Number(candidate.trade_score || 0).toFixed(1)}</span>
                                        <span className="st-scan-candidate-symbol">
                                            {candidate.symbol} · {formatRejectionReason(candidate.rejection_reason || "rank_cutoff")}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : null}
                </div>
            ) : null}

            {alternateOpportunity ? (
                <div className="st-alt-suggestion">
                    <div className="st-alt-suggestion-title">Available now</div>
                    <div className="st-alt-suggestion-copy">
                        {alternateOpportunity.symbol} has a {alternateOpportunity.label.toLowerCase()} setup right now.
                    </div>
                    <motion.button
                        className="st-btn-secondary"
                        onClick={() => onSelectSymbol?.(alternateOpportunity.symbol)}
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.97 }}
                        transition={{ duration: 0.15, ease: "easeOut" }}
                    >
                        Switch to {alternateOpportunity.symbol}
                    </motion.button>
                </div>
            ) : null}

            <DecisionBullets
                title="What to do next"
                subtitle="Simple next actions"
                items={nextSteps}
                tone="neutral"
            />

            <div className="st-ctas">
                <motion.button
                    className="st-btn-primary"
                    onClick={onConfigure}
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    transition={{ duration: 0.15, ease: "easeOut" }}
                >
                    Adjust trade settings
                </motion.button>
                <motion.button
                    className="st-btn-secondary"
                    onClick={() => onOpenSimulation?.(simulationLaunch)}
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    transition={{ duration: 0.15, ease: "easeOut" }}
                >
                    Run simulation
                </motion.button>
            </div>
        </div>
    );
}

function RiskRewardBreakdown({ decision, symbol }) {
    if (!decision?.should_trade || !decision?.meta || decision?.price == null) return null;
    const { strike_price, expiration_date, dte, delta, option_type } = decision.meta;
    const premium = Number(decision.price);
    const strike  = Number(strike_price);
    const tradeScore = Number(decision.meta?.trade_score ?? 0);
    const alternativesConsidered = Number(decision?.scan_debug?.alternatives_considered || 0);
    const qualitySummary = decision.meta?.score_components
        ? Object.entries(decision.meta.score_components)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 2)
            .map(([key]) => key.replaceAll("_", " "))
            .join(" + ")
        : null;

    if (decision.meta?.opportunity_strategy) {
        const debit = Number(decision.meta?.debit ?? decision.price ?? 0);
        const width = Number(decision.meta?.width ?? 0);
        const longStrike = Number(decision.meta?.long_strike ?? 0);
        const maxProfit = width > 0 ? Math.max(0, (width - debit) * 100) : null;
        const maxRisk = debit > 0 ? debit * 100 : null;
        const breakEven = longStrike > 0
            ? decision.meta.opportunity_strategy === "call_debit_spread"
                ? longStrike + debit
                : longStrike - debit
            : null;
        const summaryParts = [
            longStrike > 0 && decision.meta?.short_strike > 0
                ? `$${Number(longStrike).toFixed(0)} / $${Number(decision.meta.short_strike).toFixed(0)}`
                : null,
            decision.meta?.earnings_date && fmtDate(decision.meta.earnings_date),
            dte != null && `${dte}d`,
        ].filter(Boolean);

        return (
            <div className="st-risk-reward">
                {tradeScore > 0 && (
                    <div className="st-quality-banner">
                        <span className="st-quality-score">Top ranked setup · {tradeScore.toFixed(1)}/100</span>
                        <span className="st-quality-copy">
                            {qualitySummary ? `Driven by ${qualitySummary}` : "Highest quality setup in range"}
                            {alternativesConsidered > 0 ? ` · ${alternativesConsidered} alternatives considered` : ""}
                        </span>
                    </div>
                )}
                <div className="st-rr-grid">
                    <div className="st-rr-item">
                        <span className="st-rr-label">You pay</span>
                        <span className="st-rr-value">${fmtCurrency((debit || 0) * 100)}</span>
                    </div>
                    <div className="st-rr-item">
                        <span className="st-rr-label">Max profit</span>
                        <span className="st-rr-value st-rr-profit">{maxProfit != null ? `$${fmtCurrency(maxProfit)}` : "—"}</span>
                    </div>
                    <div className="st-rr-item">
                        <span className="st-rr-label">Max risk</span>
                        <span className="st-rr-value st-rr-risk">{maxRisk != null ? `−$${fmtCurrency(maxRisk)}` : "—"}</span>
                    </div>
                    <div className="st-rr-item">
                        <span className="st-rr-label">Break-even</span>
                        <span className="st-rr-value">{breakEven != null ? `$${fmtCurrency(breakEven)}` : "—"}</span>
                    </div>
                    <div className="st-rr-item">
                        <span className="st-rr-label">Spread width</span>
                        <span className="st-rr-value">{width > 0 ? `${width.toFixed(0)} pts` : "—"}</span>
                    </div>
                </div>

                {summaryParts.length > 0 && (
                    <div className="st-trade-summary-line">
                        {summaryParts.join(" · ")}
                    </div>
                )}
            </div>
        );
    }

    if (!strike || !premium) return null;

    const cashReceived = premium * 100;
    const cashRequired = strike * 100;
    const breakEven    = strike - premium;

    const expLabel = expiration_date
        ? new Date(expiration_date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })
        : null;

    const summaryParts = [
        strike_price != null && `$${strike_price}`,
        expLabel,
        dte != null && `${dte}d`,
        delta != null && `Δ ${Number(delta).toFixed(2)}`,
    ].filter(Boolean);

    return (
        <div className="st-risk-reward">
            {tradeScore > 0 && (
                <div className="st-quality-banner">
                    <span className="st-quality-score">Top ranked setup · {tradeScore.toFixed(1)}/100</span>
                    <span className="st-quality-copy">
                        {qualitySummary ? `Driven by ${qualitySummary}` : "Highest quality setup in range"}
                        {alternativesConsidered > 0 ? ` · ${alternativesConsidered} alternatives considered` : ""}
                    </span>
                </div>
            )}
            <div className="st-rr-grid">
                <div className="st-rr-item">
                    <span className="st-rr-label">You receive</span>
                    <span className="st-rr-value st-rr-positive">${fmtCurrency(cashReceived)}</span>
                </div>
                <div className="st-rr-item">
                    <span className="st-rr-label">Cash required</span>
                    <span className="st-rr-value">${fmtCurrency(cashRequired)}</span>
                </div>
                <div className="st-rr-item">
                    <span className="st-rr-label">Max profit</span>
                    <span className="st-rr-value st-rr-profit">${fmtCurrency(cashReceived)}</span>
                </div>
                <div className="st-rr-item">
                    <span className="st-rr-label">Break-even</span>
                    <span className="st-rr-value">${fmtCurrency(breakEven)}</span>
                </div>
                <div className="st-rr-item">
                    <span className="st-rr-label">Max risk</span>
                    <span className="st-rr-value st-rr-risk">−${fmtCurrency(breakEven * 100)}</span>
                </div>
            </div>

            {summaryParts.length > 0 && (
                <div className="st-trade-summary-line">
                    {summaryParts.join(" · ")}
                </div>
            )}

            {option_type === "put" && strike_price && (
                <div className="st-assignment-note">
                    If assigned, you will buy 100 shares of {symbol} at ${strike_price}
                </div>
            )}
            {option_type === "call" && strike_price && (
                <div className="st-assignment-note">
                    If assigned, your shares will be called away at ${strike_price}
                </div>
            )}
        </div>
    );
}

function ConfirmTradeModal({ decision, symbol, onConfirm, onCancel, submitting }) {
    if (!decision?.meta) return null;
    const premium  = Number(decision.price || 0);
    const strike   = Number(decision.meta?.strike_price ?? 0);
    const isSpread = Boolean(decision.meta?.opportunity_strategy);
    const longStrike = Number(decision.meta?.long_strike ?? 0);
    const shortStrike = Number(decision.meta?.short_strike ?? 0);
    const width = Number(decision.meta?.width ?? 0);
    const maxProfit = isSpread && width > 0 ? Math.max(0, (width - premium) * 100) : null;

    return (
        <motion.div
            className="st-modal-overlay"
            onClick={onCancel}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
        >
            <motion.div
                className="st-modal"
                onClick={(e) => e.stopPropagation()}
                initial={{ opacity: 0, scale: 0.95, y: 12 }}
                animate={{ opacity: 1, scale: 1,    y: 0  }}
                exit={{ opacity: 0, scale: 0.95, y: 12 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
            >
                <div className="st-modal-title">Confirm Trade</div>
                <div className="st-modal-body">
                    <div className="st-modal-line">
                        <span className="st-modal-line-label">{isSpread ? "You will pay" : "You will receive"}</span>
                        <span className={`st-modal-line-val ${isSpread ? "" : "st-modal-positive"}`}>
                            ${fmtCurrency(premium * 100)}
                        </span>
                    </div>
                    {isSpread ? (
                        <>
                            <div className="st-modal-line">
                                <span className="st-modal-line-label">Spread</span>
                                <span className="st-modal-line-val">
                                    {longStrike > 0 && shortStrike > 0
                                        ? `$${fmtCurrency(longStrike)} / $${fmtCurrency(shortStrike)}`
                                        : "—"}
                                </span>
                            </div>
                            <div className="st-modal-line">
                                <span className="st-modal-line-label">Max profit</span>
                                <span className="st-modal-line-val st-modal-positive">
                                    {maxProfit != null ? `$${fmtCurrency(maxProfit)}` : "—"}
                                </span>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="st-modal-line">
                                <span className="st-modal-line-label">You will reserve</span>
                                <span className="st-modal-line-val">${fmtCurrency(strike * 100)}</span>
                            </div>
                            <div className="st-modal-line">
                                <span className="st-modal-line-label">You may be assigned</span>
                                <span className="st-modal-line-val">100 shares at ${decision.meta?.strike_price}</span>
                            </div>
                        </>
                    )}
                </div>
                <div className="st-modal-actions">
                    <motion.button
                        className="st-modal-confirm"
                        onClick={onConfirm}
                        disabled={submitting}
                        whileHover={{ scale: submitting ? 1 : 1.02 }}
                        whileTap={{ scale: submitting ? 1 : 0.97 }}
                        transition={{ duration: 0.15, ease: "easeOut" }}
                    >
                        {submitting ? (
                            <span className="st-btn-content">
                                <InlineSpinner />
                                Re-evaluating market conditions…
                            </span>
                        ) : "Confirm"}
                    </motion.button>
                    <motion.button
                        className="st-modal-cancel"
                        onClick={onCancel}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.97 }}
                        transition={{ duration: 0.15, ease: "easeOut" }}
                    >
                        Cancel
                    </motion.button>
                </div>
            </motion.div>
        </motion.div>
    );
}

function TradeSkeleton() {
    return (
        <div className="st-skeleton-body">
            <div className="st-skeleton" style={{ width: "52%", height: 22, borderRadius: 6 }} />
            <div className="st-skeleton-rr">
                {[...Array(5)].map((_, i) => (
                    <div key={i} className="st-skeleton-rr-item">
                        <div className="st-skeleton" style={{ width: 56, height: 10, borderRadius: 4 }} />
                        <div className="st-skeleton" style={{ width: 72, height: 15, borderRadius: 5 }} />
                    </div>
                ))}
            </div>
            <div className="st-skeleton" style={{ width: "70%", height: 11, borderRadius: 4 }} />
            <div className="st-skeleton-ctas">
                <div className="st-skeleton" style={{ width: 140, height: 44, borderRadius: 10 }} />
                <div className="st-skeleton" style={{ width: 100, height: 44, borderRadius: 10 }} />
            </div>
        </div>
    );
}

function InlineSpinner() {
    return <span className="st-inline-spinner" aria-hidden="true" />;
}

// ------------------------------------------------------------------ //
// Component                                                            //
// ------------------------------------------------------------------ //

const SuggestedTrade = ({ symbol, portfolioValue, buyingPower, dailyPnL, strategyStatus, portfolioConfig, accountSource = "trader", onConfigure, onOpenSimulation, onSelectSymbol, hasQueuedOrders, onTradeExecuted, onStrategyChange }) => {
    const [decision,         setDecision]         = useState(null);
    const [loading,          setLoading]           = useState(true);
    const [error,            setError]             = useState(null);
    const [executing,        setExecuting]         = useState(false);
    const [result,           setResult]            = useState(null);
    const [showConfirmModal, setShowConfirmModal]  = useState(false);
    const [showStrategyPicker, setShowStrategyPicker] = useState(false);
    const [alternateOpportunity, setAlternateOpportunity] = useState(null);
    const [earningsData, setEarningsData] = useState(null);
    const [previewTimestamp, setPreviewTimestamp] = useState(null);
    const [driftWarning,     setDriftWarning]     = useState(null);
    const smallAccountMode = isSmallAccountMode(buyingPower);
    const strategyAllowed = isStrategyAllowedForBuyingPower(portfolioConfig?.strategy, buyingPower, portfolioValue);

    useEffect(() => {
        let cancelled = false;

        const fetchEarningsData = async () => {
            if (portfolioConfig?.strategy !== "earnings" || !symbol || !strategyAllowed) {
                setEarningsData(null);
                return;
            }
            try {
                const data = await fetchEarningsOpportunity(symbol, portfolioValue, accountSource);
                if (!cancelled) {
                    setEarningsData(data || null);
                }
            } catch {
                if (!cancelled) {
                    setEarningsData(null);
                }
            }
        };

        fetchEarningsData();
        return () => {
            cancelled = true;
        };
    }, [accountSource, portfolioConfig?.strategy, symbol, strategyAllowed]);

    useEffect(() => { fetchPreview(); }, [accountSource, symbol, portfolioConfig?.strategy, buyingPower]);

    // Keep configure screen in sync with the strategy the trade card reports.
    useEffect(() => {
        const cardStrategy = decision?.meta?.strategy;
        if (cardStrategy && cardStrategy !== portfolioConfig?.strategy) {
            onStrategyChange?.(cardStrategy);
        }
    }, [decision?.meta?.strategy]);

    const requestPreview = async (targetSymbol, strategy) => {
        const { data } = await axios.post(
            buildApiUrl("/execute-strategy"),
            {
                symbol: targetSymbol,
                strategy,
                account_source: accountSource,
                preset: "balanced",
                automation_mode: "manual",
                portfolio_value: portfolioValue ?? 0,
                portfolio_context: {
                    portfolio_value: portfolioValue ?? 0,
                    buying_power: buyingPower ?? 0,
                    account_source: accountSource,
                    strategy,
                    source: "suggested_trade_preview",
                },
                config: { max_capital_per_trade: 5000, take_profit_pct: 0.5, roll_threshold: 2.0 },
                dry_run: true,
            },
            { withCredentials: true }
        );
        return data?.decision
            ? {
                ...data.decision,
                scan_debug: data?.scan_debug ?? null,
            }
            : null;
    };

    const findAlternateOpportunity = async (strategy, activeSymbol) => {
        const candidates = (ALTERNATE_SYMBOL_CANDIDATES[strategy] || []).filter(
            (candidate) => candidate !== activeSymbol
        );
        for (const candidate of candidates) {
            try {
                const altDecision = await requestPreview(candidate, strategy);
                if (altDecision?.should_trade) {
                    return {
                        symbol: candidate,
                        decision: altDecision,
                        label: formatStrategyLabel(strategy),
                    };
                }
            } catch {
                // Ignore candidate failures and keep scanning a short curated list.
            }
        }
        return null;
    };

    const fetchPreview = async () => {
        const strategy = portfolioConfig?.strategy;
        if (!strategy) {
            setLoading(false);
            setDecision(null);
            setAlternateOpportunity(null);
            setError("Choose a strategy before previewing a trade.");
            return;
        }
        if (!strategyAllowed) {
            setLoading(false);
            setDecision(null);
            setAlternateOpportunity(null);
            setError(getStrategyBlockMessage(portfolioConfig?.strategy, buyingPower, portfolioValue) || SMALL_ACCOUNT_REJECTION_MESSAGE);
            return;
        }
        setLoading(true);
        setDecision(null);
        setError(null);
        setResult(null);
        setAlternateOpportunity(null);
        setDriftWarning(null);
        try {
            const nextDecision = await requestPreview(symbol, strategy);
            setDecision(nextDecision);
            setPreviewTimestamp(new Date());
            if (!nextDecision?.should_trade && strategy !== "wheel") {
                const alternate = await findAlternateOpportunity(strategy, symbol);
                setAlternateOpportunity(alternate);
            }
        } catch (err) {
            const raw = err.response?.data?.details || err.response?.data?.error || err.message || "";
            setError(parseApiError(raw));
        } finally {
            setLoading(false);
        }
    };

    const handleExecute = async () => {
        const strategy =
            displayDecision?.meta?.strategy
            || decision?.meta?.strategy
            || portfolioConfig?.strategy;
        if (!strategy) {
            toast.error("Choose a strategy before executing a trade.");
            return;
        }
        if (strategy !== portfolioConfig?.strategy) {
            console.log(
                `[exec] strategy override: card="${strategy}" config="${portfolioConfig?.strategy}" → using "${strategy}"`
            );
        }
        if (!isStrategyAllowedForBuyingPower(strategy, buyingPower, portfolioValue)) {
            toast.error(getStrategyBlockMessage(strategy, buyingPower, portfolioValue) || SMALL_ACCOUNT_REJECTION_MESSAGE);
            return;
        }
        const capturedPreviewPrice = displayDecision?.price;
        setExecuting(true);
        setDriftWarning(null);
        fireTradeLoading();
        try {
            const { data } = await axios.post(
                buildApiUrl("/execute-strategy"),
            {
                symbol,
                strategy,
                account_source: accountSource,
                preset: "balanced",
                automation_mode: "manual",
                portfolio_value: portfolioValue ?? 0,
                portfolio_context: {
                    portfolio_value: portfolioValue ?? 0,
                    buying_power: buyingPower ?? 0,
                    account_source: accountSource,
                    strategy,
                    source: "suggested_trade",
                },
                    config: { max_capital_per_trade: 5000, take_profit_pct: 0.5, roll_threshold: 2.0 },
                    dry_run: false,
                },
                { withCredentials: true, timeout: EXECUTION_TIMEOUT_MS }
            );
            const execution = {
                ...data.execution,
                ...(data.earnings_preview_only ? { earnings_preview_only: true } : {}),
            };
            const execPrice = data.decision?.price;
            if (
                capturedPreviewPrice != null &&
                execPrice != null &&
                Math.abs(capturedPreviewPrice) > 0
            ) {
                const drift = Math.abs(execPrice - capturedPreviewPrice) / Math.abs(capturedPreviewPrice);
                if (drift > DRIFT_THRESHOLD) {
                    setDriftWarning({
                        previewPrice: capturedPreviewPrice,
                        execPrice,
                        driftPct: Math.round(drift * 100),
                    });
                }
            }
            setResult(execution);
            const toastFeedback = buildExecutionToastMessage(execution, data);
            fireTradeToast(toastFeedback.message, toastFeedback.type);
            if (execution?.status === "submitted") {
                onTradeExecuted?.("submitted");
            } else if (execution?.status === "queued") {
                onTradeExecuted?.("queued");
            }
            setShowConfirmModal(false);
        } catch (err) {
            const reason = formatExecutionError(err, "Execution failed.");
            setResult({ status: "error", error: reason });
            fireTradeToast(reason, "error");
        } finally {
            setExecuting(false);
        }
    };

    const strategyLabel = formatStrategyLabel(portfolioConfig?.strategy);
    const displayDecision = portfolioConfig?.strategy === "earnings"
        ? buildEarningsDisplayDecision(symbol, earningsData, decision)
        : buildDisplayDecision(decision, earningsData);
    const actionLabel = displayDecision?.meta?.opportunity_strategy
        ? formatOpportunityStrategyLabel(displayDecision.meta.opportunity_strategy)
        : ACTION_LABELS[displayDecision?.action] || displayDecision?.action;
    const reason      = displayDecision?.reason || displayDecision?.meta?.reason;
    const whyTrade = buildWhyTrade(displayDecision, symbol);
    const risks = buildRisks(displayDecision, symbol, hasQueuedOrders);
    const _cardStrategy     = displayDecision?.meta?.strategy;
    const _configStrategy   = portfolioConfig?.strategy;
    const _effectiveStrategy = _cardStrategy || _configStrategy;
    if (_cardStrategy && _cardStrategy !== _configStrategy) {
        console.log(
            `[sim] strategy override: card="${_cardStrategy}" config="${_configStrategy}" → using "${_cardStrategy}"`
        );
    }
    const tradeSimulationLaunch = displayDecision
        ? buildSimulationLaunchRequest(
            displayDecision,
            symbol,
            portfolioValue,
            buyingPower,
            _effectiveStrategy,
            accountSource
        )
        : null;
    const pnlPositive = dailyPnL >= 0;
    const contentKey  = loading ? "loading" : error ? "error" : displayDecision ? (displayDecision.action ?? "no-trade") : "empty";

    const guidanceText = !loading && !error && displayDecision
        ? displayDecision.should_trade
            ? "Suggested action: Review and execute your next cash-secured put"
            : `No open positions — start your ${strategyLabel.toLowerCase()} below`
        : null;

    return (
        <section className="suggested-trade">
            <PortfolioStatusCard
                buyingPower={buyingPower}
                dailyPnL={dailyPnL}
                hasQueuedOrders={hasQueuedOrders}
                strategyStatus={strategyStatus}
                accountSource={accountSource}
                onEditStrategy={() => setShowStrategyPicker((open) => !open)}
            />

            {/* Trade card */}
            <motion.div
                className="st-card"
                variants={cardVariants}
                initial="hidden"
                animate="visible"
                whileHover="hover"
                whileTap="tap"
            >
                <div className="st-card-top">
                    <div className="st-symbol-block">
                        <span className="st-symbol">{symbol}</span>
                        {previewTimestamp && !loading && (
                            <span className="st-preview-ts">Preview · {fmtTime(previewTimestamp)}</span>
                        )}
                        <div className="st-badge-wrap">
                            <span className="st-badge">{strategyLabel.replace(" Strategy", "")}</span>
                            {smallAccountMode ? <span className="st-small-account-badge">Small Account Mode</span> : null}
                            <button
                                type="button"
                                className="st-badge-edit"
                                onClick={() => setShowStrategyPicker((open) => !open)}
                                title="Choose strategy"
                                aria-label="Choose strategy"
                            >
                                ✎
                            </button>
                            <AnimatePresence>
                                {showStrategyPicker && (
                                    <motion.div
                                        className="st-strategy-picker"
                                        initial={{ opacity: 0, y: 6 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: 4 }}
                                        transition={{ duration: 0.14, ease: "easeOut" }}
                                    >
                                        {STRATEGY_OPTIONS.map((option) => (
                                            <button
                                                key={option.id}
                                                type="button"
                                                title={!isStrategyAllowedForBuyingPower(option.id, buyingPower, portfolioValue) ? SMALL_ACCOUNT_TOOLTIP : ""}
                                                disabled={!isStrategyAllowedForBuyingPower(option.id, buyingPower, portfolioValue)}
                                                className={`st-strategy-option ${portfolioConfig?.strategy === option.id ? "active" : ""} ${!isStrategyAllowedForBuyingPower(option.id, buyingPower, portfolioValue) ? "disabled" : ""}`}
                                                onClick={() => {
                                                    if (!isStrategyAllowedForBuyingPower(option.id, buyingPower, portfolioValue)) {
                                                        setError(SMALL_ACCOUNT_REJECTION_MESSAGE);
                                                        setShowStrategyPicker(false);
                                                        return;
                                                    }
                                                    onStrategyChange?.(option.id);
                                                    setShowStrategyPicker(false);
                                                }}
                                            >
                                                {option.label}
                                                {option.id === "earnings" && EARNINGS_PREVIEW_ONLY && (
                                                    <span className="st-preview-badge">Preview</span>
                                                )}
                                            </button>
                                        ))}
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </div>
                    <motion.button
                        className="st-refresh"
                        onClick={fetchPreview}
                        disabled={loading}
                        title="Refresh"
                        whileHover={{ scale: 1.15 }}
                        whileTap={{ scale: 0.88 }}
                        transition={{ duration: 0.15, ease: "easeOut" }}
                    >
                        ↻
                    </motion.button>
                </div>

                <AnimatePresence mode="wait">
                    {loading && (
                        <motion.div key="loading" variants={contentVariants} initial="hidden" animate="visible" exit="exit">
                            <TradeSkeleton />
                        </motion.div>
                    )}

                    {error && !loading && (
                        <motion.div key="error" variants={contentVariants} initial="hidden" animate="visible" exit="exit">
                            <div className="st-error">{error}</div>
                        </motion.div>
                    )}

                    {displayDecision && !loading && !error && (
                        <motion.div key={contentKey} variants={contentVariants} initial="hidden" animate="visible" exit="exit">
                            {portfolioConfig?.strategy === "earnings" && (
                                <EarningsStatusNotice symbol={symbol} earningsData={earningsData} />
                            )}
                            {displayDecision.should_trade ? (
                                <div className="st-trade">
                                    <DecisionScore decision={displayDecision} />

                                    <EarningsSummary decision={displayDecision} earningsData={earningsData} />

                                    <div className="st-action-label">
                                        {actionLabel}
                                    </div>

                                    {displayDecision.meta?.small_account_mode && (
                                        <div className="st-small-account-badge">
                                            Optimized for small account growth
                                        </div>
                                    )}

                                    <RiskRewardBreakdown decision={displayDecision} symbol={symbol} />

                                    <DecisionBullets
                                        title="Why this trade"
                                        subtitle="What looks attractive here"
                                        items={whyTrade}
                                        tone="positive"
                                    />

                                    <DecisionBullets
                                        title="Risks"
                                        subtitle="What to watch before you place it"
                                        items={risks}
                                        tone="warning"
                                    />

                                    <TradeSimPreview decision={displayDecision} portfolioValue={portfolioValue} />

                                    {reason && <div className="st-reason">{reason}</div>}

                                    {portfolioConfig?.strategy === "wheel" && getWheelCapitalWarning(buyingPower, portfolioValue) && (
                                        <div className="st-wheel-warning">
                                            {getWheelCapitalWarning(buyingPower, portfolioValue)}
                                        </div>
                                    )}

                                    {!result && (
                                        <div className="st-ctas">
                                            <motion.button
                                                className="st-btn-primary"
                                                onClick={() => setShowConfirmModal(true)}
                                                disabled={executing}
                                                whileHover={{ scale: executing ? 1 : 1.03 }}
                                                whileTap={{ scale: executing ? 1 : 0.97 }}
                                                transition={{ duration: 0.15, ease: "easeOut" }}
                                            >
                                                {executing ? (
                                                    <span className="st-btn-content">
                                                        <InlineSpinner />
                                                        Submitting...
                                                    </span>
                                                ) : portfolioConfig?.strategy === "earnings" && EARNINGS_PREVIEW_ONLY
                                                    ? "Preview Trade"
                                                    : hasQueuedOrders ? "Queue Trade" : "Execute Trade"}
                                            </motion.button>
                                            <motion.button
                                                className="st-btn-secondary"
                                                onClick={() => onOpenSimulation?.(tradeSimulationLaunch)}
                                                whileHover={{ scale: 1.03 }}
                                                whileTap={{ scale: 0.97 }}
                                                transition={{ duration: 0.15, ease: "easeOut" }}
                                            >
                                                Run simulation
                                            </motion.button>
                                            <motion.button
                                                className="st-btn-secondary"
                                                onClick={onConfigure}
                                                whileHover={{ scale: 1.03 }}
                                                whileTap={{ scale: 0.97 }}
                                                transition={{ duration: 0.15, ease: "easeOut" }}
                                            >
                                                Configure
                                            </motion.button>
                                        </div>
                                    )}

                                    {driftWarning && (
                                        <motion.div
                                            className="st-drift-warning"
                                            initial={{ opacity: 0, y: 4 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.2, ease: "easeOut" }}
                                        >
                                            Pricing moved {driftWarning.driftPct}% since preview
                                            &nbsp;(${fmtCurrency(driftWarning.previewPrice * 100)} → ${fmtCurrency(driftWarning.execPrice * 100)}).
                                            Review before proceeding.
                                        </motion.div>
                                    )}

                                    {result && (
                                        <motion.div
                                            className={`st-result st-result--${result.status}`}
                                            initial={{ opacity: 0, y: 6 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.2, ease: "easeOut" }}
                                        >
                                            {result.status === "submitted" && `Order submitted · ${result.order_id}`}
                                            {result.status === "queued"    && "Queued — will execute when market opens at 9:30 AM"}
                                            {result.status === "dry_run"   && (
                                                result.earnings_preview_only
                                                    ? "Preview complete — earnings strategy is in preview-only mode for the MVP. No order placed."
                                                    : "Dry run complete — no order placed"
                                            )}
                                            {result.status === "skipped"   && (result.message || "Skipped")}
                                            {result.status === "error"     && (result.error || "Execution failed")}
                                        </motion.div>
                                    )}
                                </div>
                            ) : (
                                <NoTradeInsightCard
                                    decision={displayDecision}
                                    onConfigure={onConfigure}
                                    onOpenSimulation={onOpenSimulation}
                                    simulationLaunch={tradeSimulationLaunch}
                                    alternateOpportunity={alternateOpportunity}
                                    onSelectSymbol={onSelectSymbol}
                                />
                            )}
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.div>

            {/* Savvy Insight — shown after a tradeable decision loads */}
            <AnimatePresence>
                {displayDecision?.should_trade && !loading && !error && (
                    <motion.div
                        key={`insight-${displayDecision?.action}`}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25, ease: "easeOut", delay: 0.1 }}
                    >
                        <SavvyInsight
                            decision={displayDecision}
                            symbol={symbol}
                            portfolioValue={portfolioValue}
                            strategy={portfolioConfig?.strategy}
                        />
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Guidance line */}
            <AnimatePresence>
                {guidanceText && (
                    <motion.div
                        className="st-guidance"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeOut", delay: 0.1 }}
                    >
                        {guidanceText}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Confirmation modal */}
            <AnimatePresence>
                {showConfirmModal && (
                    <ConfirmTradeModal
                        decision={displayDecision}
                        symbol={symbol}
                        onConfirm={handleExecute}
                        onCancel={() => setShowConfirmModal(false)}
                        submitting={executing}
                    />
                )}
            </AnimatePresence>
        </section>
    );
};

export default SuggestedTrade;
